// src/routes/domains/newzealand/nz.routes.tsx
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Public marketing hub (eager, if exists)
// ------------------------------
// import NZImmigrationHub from '@/pages/marketing/NZImmigrationHub';

// ------------------------------
// Lazy‑loaded New Zealand components (absolute imports)
// ------------------------------

// Core ecosystem & flight deck
const NZToolsEcosystem = lazy(() => import('@/pages/NewZealandToolsEcosystem'));
const NZSkilledCompanion = lazy(() => import('@/pages/journey/NZSkilledCompanion'));
const NZCompanionResults = lazy(() => import('@/pages/journey/NZCompanionResults'));
const NZImmigrationFlightDeck = lazy(() => import('@/pages/NewZealandImmigrationFlightDeck'));
const NZEvisaGateway = lazy(() => import('@/pages/NZeVisaGateway'));
const NZVisaFormConductor = lazy(() => import('@/pages/NZVisaFormConductor'));
const NZJourneyMap = lazy(() => import('@/pages/NZJourneyMap'));
const NZSavedJourneyMaps = lazy(() => import('@/pages/NZSavedJourneyMaps'));
const NZReunificationAccelerator = lazy(() => import('@/pages/NZReunificationAccelerator'));

// Assessments
const NZSkilledMigrantAssessment = lazy(() => import('@/pages/NZSkilledMigrantAssessment'));
const NZGreenListStraightToResidenceAssessment = lazy(() => import('@/pages/NZGreenListStraightToResidenceAssessment'));
const NZGreenListWorkToResidenceAssessment = lazy(() => import('@/pages/NZGreenListWorkToResidenceAssessment'));
const NZAccreditedEmployerWorkVisaAssessment = lazy(() => import('@/pages/NZAccreditedEmployerWorkVisaAssessment'));
const NZPartnerOfAWorkerAssessment = lazy(() => import('@/pages/NZPartnerOfAWorkerAssessment'));
const NZStudentVisaAssessment = lazy(() => import('@/pages/NZStudentVisaAssessment'));
const NZVisitorVisaAssessment = lazy(() => import('@/pages/NZVisitorVisaAssessment'));
const NZAssessmentResultAnalysis = lazy(() => import('@/pages/NZAssessmentResultAnalysis'));

// Pathway & planning tools
const NZPathwayDreamsEngine = lazy(() => import('@/pages/NZPathwayDreamsEngine'));
const NZITASimulator = lazy(() => import('@/pages/NZITASimulator'));
const NZSuccessPrediction = lazy(() => import('@/pages/NZSuccessPrediction'));
const NZTimelineForecaster = lazy(() => import('@/pages/NZTimelineForecaster'));
const NZDependencyOptimizer = lazy(() => import('@/pages/NZDependencyOptimizer'));
const NZPathwayEngine = lazy(() => import('@/pages/NZPathwayEngine'));
const NZSkilledMigrantPoints = lazy(() => import('@/pages/NZSkilledMigrantPoints'));

// Document & validation tools — theatrical AI validators replaced by honest guides
const NZDocumentRequirementGuide = lazy(() => import('@/pages/NZDocumentRequirementGuide'));
const NZVerificationDashboard = lazy(() => import('@/pages/NZVerificationDashboard'));
const NZDocumentCompletenessChecker = lazy(() => import('@/pages/NZDocumentCompletenessChecker'));
const NZNZQAECATool = lazy(() => import('@/pages/NZNZQAECATool'));
const NZWorkRefLetterChecker = lazy(() => import('@/pages/NZWorkRefLetterChecker'));

// Calculators & checkers
const NZMaintenanceFundsCalculator = lazy(() => import('@/pages/NZMaintenanceFundsCalculator'));
const NZDependentVisaCalculator = lazy(() => import('@/pages/NZDependentVisaCalculator'));
const NZGlobalSalaryCalculator = lazy(() => import('@/pages/NZGlobalSalaryCalculator'));
const NZTaxEstimator = lazy(() => import('@/pages/NZTaxEstimator'));
const NZHealthCoverage = lazy(() => import('@/pages/NZHealthCoverage'));
const NZCitizenshipEligibility = lazy(() => import('@/pages/NZCitizenshipEligibility'));
const NZRegionComparison = lazy(() => import('@/pages/NZRegionComparison'));
const NZAccreditedEmployerDatabase = lazy(() => import('@/pages/NZAccreditedEmployerDatabase'));
const NZJobBank = lazy(() => import('@/pages/NZJobBank'));
const NZOccupationDemandChecker = lazy(() => import('@/pages/NZOccupationDemandChecker'));
const NZBiometricAppointment = lazy(() => import('@/pages/NZBiometricAppointment'));
const NZResidenceFromWork = lazy(() => import('@/pages/NZResidenceFromWork'));
const NZBiometricValidator = lazy(() => import('@/pages/NZBiometricValidator'));
const NZAccommodationChecker = lazy(() => import('@/pages/NZAccommodationChecker'));
const NZDLIValidator = lazy(() => import('@/pages/NZDLIValidator'));
const NZRiskAssessment = lazy(() => import('@/pages/NZRiskAssessment'));
const NZWorkToResidence = lazy(() => import('@/pages/NZWorkToResidence'));
const NZAccreditedEmployer = lazy(() => import('@/pages/NZAccreditedEmployer'));
const NZStudyPermit = lazy(() => import('@/pages/NZStudyPermit'));
const NZBusinessVisitorGuide = lazy(() => import('@/pages/NZBusinessVisitorGuide'));
const NZRegionalVisaProcessing = lazy(() => import('@/pages/NZRegionalVisaProcessing'));
const NZImmigrationTechData = lazy(() => import('@/pages/NZImmigrationTechData'));
const NZYouthStudentTools = lazy(() => import('@/pages/NZYouthStudentTools'));
const NZAccommodationHousing = lazy(() => import('@/pages/NZAccommodationHousing'));
const NZVisaExpiryExtension = lazy(() => import('@/pages/NZVisaExpiryExtension'));
const NZPermanentResidenceTools = lazy(() => import('@/pages/NZPermanentResidenceTools'));
const NZGreenListFinder = lazy(() => import('@/pages/NZGreenListFinder'));
const NZFamilySponsor = lazy(() => import('@/pages/NZFamilySponsor'));
const NZStraightToResidence = lazy(() => import('@/pages/NZStraightToResidence'));
const NZPathwayAnalytics = lazy(() => import('@/pages/NZPathwayAnalytics'));
const NZTemporaryWorkerRoutes = lazy(() => import('@/pages/NZTemporaryWorkerRoutes'));

// Phase B — 17 components built to cover the 21 previously-unbuilt
// ecosystem entries (4 components each serve two route paths for
// near-duplicate ecosystem entries; see comments in each file).
const NZProfessionalHealthcareRoutes = lazy(() => import('@/pages/NZProfessionalHealthcareRoutes'));
const NZDependantFamilyTools = lazy(() => import('@/pages/NZDependantFamilyTools'));
const NZCaregiverPathways = lazy(() => import('@/pages/NZCaregiverPathways'));
const NZAgriFoodSeasonalWork = lazy(() => import('@/pages/NZAgriFoodSeasonalWork'));
const NZSelfEmployedEntrepreneur = lazy(() => import('@/pages/NZSelfEmployedEntrepreneur'));
const NZVisaFeeCalculator = lazy(() => import('@/pages/NZVisaFeeCalculator'));
const NZDocumentAuthentication = lazy(() => import('@/pages/NZDocumentAuthentication'));
const NZSkilledMigrantEligibility = lazy(() => import('@/pages/NZSkilledMigrantEligibility'));
const NZGreenListTrades = lazy(() => import('@/pages/NZGreenListTrades'));
const NZRegionalRuralWork = lazy(() => import('@/pages/NZRegionalRuralWork'));
const NZTranslationGuide = lazy(() => import('@/pages/NZTranslationGuide'));
const NZEnglishRequirement = lazy(() => import('@/pages/NZEnglishRequirement'));
const NZPointsCalculatorAlt = lazy(() => import('@/pages/NZPointsCalculatorAlt'));
const NZDrawHistory = lazy(() => import('@/pages/NZDrawHistory'));
const NZInzWebformHelper = lazy(() => import('@/pages/NZInzWebformHelper'));
const NZPanelPhysicianFinder = lazy(() => import('@/pages/NZPanelPhysicianFinder'));
const NZImmigrationEligibilityQuiz = lazy(() => import('@/pages/NZImmigrationEligibilityQuiz'));

// ------------------------------
// New Zealand Routes Definition
// ------------------------------
export const nzRoutes: AppRoute[] = [
  // Optional public marketing hub (uncomment if needed)
  // {
  //   id: 'nz.marketing_hub',
  //   domain: 'newzealand',
  //   path: '/nz-immigration-hub',
  //   element: createProtectedRoute(<NZImmigrationHub />, {
  //     protected: false,
  //     meta: { title: 'New Zealand Immigration Hub', region: 'NZ', intent: 'guide' },
  //   }),
  // },

  // Core ecosystem & flight deck
  {
    id: 'nz.tools_ecosystem',
    domain: 'newzealand',
    path: '/nz-tools-ecosystem',
    element: createProtectedRoute(<NZToolsEcosystem />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Tools Ecosystem',
        region: 'NZ',
        intent: 'dashboard',
        aiTags: ['new zealand', 'ecosystem', 'tools'],
      },
    }),
  },
  {
    id: 'nz.skilled_companion',
    domain: 'newzealand',
    path: '/nz-skilled-companion',
    element: createProtectedRoute(<NZSkilledCompanion />, {
      tier: 'premium',
      meta: {
        title: 'NZ Skilled Companion',
        region: 'NZ',
        intent: 'companion',
        aiTags: ['nz', 'companion', 'blueprint'],
        toolId: 'nz-skilled-companion',
        category: 'companion',
      },
    }),
  },
  {
    id: 'nz.companion_results',
    domain: 'newzealand',
    path: '/nz-companion-results',
    element: createProtectedRoute(<NZCompanionResults />, {
      tier: 'premium',
      meta: {
        title: 'NZ Companion Results',
        region: 'NZ',
        intent: 'companion',
        aiTags: ['nz', 'companion', 'vault'],
        toolId: 'nz-companion-results',
        category: 'companion',
      },
    }),
  },
  {
    id: 'nz.flight_deck',
    domain: 'newzealand',
    path: '/new-zealand-immigration-flight-deck',
    element: createProtectedRoute(<NZImmigrationFlightDeck />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Immigration Flight Deck',
        region: 'NZ',
        intent: 'dashboard',
        aiTags: ['new zealand', 'flight deck', 'overview'],
      },
    }),
  },
  {
    id: 'nz.evisa_gateway',
    domain: 'newzealand',
    path: '/nz-evisa-gateway',
    element: createProtectedRoute(<NZEvisaGateway />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand eVisa Gateway',
        region: 'NZ',
        intent: 'gateway',
        aiTags: ['evisa', 'gateway', 'nz'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'evisa-gateway'.
        toolId: 'nz-evisa-gateway',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.visa_form',
    domain: 'newzealand',
    path: '/nz-visa-form-conductor',
    element: createProtectedRoute(<NZVisaFormConductor />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Visa Form Conductor',
        region: 'NZ',
        intent: 'form',
        aiTags: ['visa', 'form', 'application'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'visa-form-conductor'.
        toolId: 'nz-visa-form-conductor',
        category: 'documents',
      },
    }),
  },
  {
    id: 'nz.journey_map',
    domain: 'newzealand',
    path: '/nz-journey-map',
    element: createProtectedRoute(<NZJourneyMap />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Journey Map',
        region: 'NZ',
        intent: 'map',
        aiTags: ['journey', 'roadmap', 'nz'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'journey-map'.
        toolId: 'nz-journey-map',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.saved_journey_maps',
    domain: 'newzealand',
    path: '/nz-saved-journey-maps',
    element: createProtectedRoute(<NZSavedJourneyMaps />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Saved Journey Maps',
        region: 'NZ',
        intent: 'storage',
        aiTags: ['saved', 'journey', 'user data'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'saved-journey-maps'.
        toolId: 'nz-saved-journey-maps',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.reunification_accelerator',
    domain: 'newzealand',
    path: '/nz-reunification-accelerator',
    element: createProtectedRoute(<NZReunificationAccelerator />, {
      tier: 'premium',
      meta: {
        title: 'New Zealand Reunification Accelerator',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['reunification', 'family', 'accelerator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'reunification-accelerator'.
        toolId: 'nz-reunification-accelerator',
        category: 'assessments',
      },
    }),
  },
  {
    // Retired 2026-08-29 — unified into /interview-simulator (all countries, one shared
    // trainer). This component's own content was real and honest and was ported into the
    // shared embassy question bank rather than discarded. See PROJECT_HISTORY.md.
    id: 'nz.interview_simulator',
    domain: 'newzealand',
    path: '/nz-interview-simulator',
    element: <Navigate to="/interview-simulator" replace />,
  },

  // Assessments
  {
    id: 'nz.skilled_migrant',
    domain: 'newzealand',
    path: '/nz-skilled-migrant-assessment',
    element: createProtectedRoute(<NZSkilledMigrantAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Skilled Migrant Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['skilled migrant', 'smc', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'skilled-migrant-assessment'.
        toolId: 'nz-skilled-migrant-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.green_list_str',
    domain: 'newzealand',
    path: '/nz-green-list-straight-to-residence-assessment',
    element: createProtectedRoute(<NZGreenListStraightToResidenceAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Green List – Straight to Residence Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['green list', 'straight to residence', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'green-list-straight-residence'.
        toolId: 'nz-green-list-straight-residence',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.green_list_wtr',
    domain: 'newzealand',
    path: '/nz-green-list-work-to-residence-assessment',
    element: createProtectedRoute(<NZGreenListWorkToResidenceAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Green List – Work to Residence Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['green list', 'work to residence', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'green-list-work-residence'.
        toolId: 'nz-green-list-work-residence',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.aewv',
    domain: 'newzealand',
    path: '/nz-accredited-employer-work-visa-assessment',
    element: createProtectedRoute(<NZAccreditedEmployerWorkVisaAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Accredited Employer Work Visa Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['aewv', 'accredited employer', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'accredited-employer-assessment'.
        toolId: 'nz-accredited-employer-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.partner_of_worker',
    domain: 'newzealand',
    path: '/nz-partner-of-a-worker-assessment',
    element: createProtectedRoute(<NZPartnerOfAWorkerAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Partner of a Worker Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['partner', 'worker', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'partner-worker-assessment'.
        toolId: 'nz-partner-worker-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.student_assessment',
    domain: 'newzealand',
    path: '/nz-student-visa-assessment',
    element: createProtectedRoute(<NZStudentVisaAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Student Visa Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['student', 'visa', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'student-visa-assessment'.
        toolId: 'nz-student-visa-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.visitor_assessment',
    domain: 'newzealand',
    path: '/nz-visitor-visa-assessment',
    element: createProtectedRoute(<NZVisitorVisaAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Visitor Visa Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['visitor', 'visa', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'visitor-visa-assessment'.
        toolId: 'nz-visitor-visa-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.assessment_result',
    domain: 'newzealand',
    path: '/nz-assessment-result-analysis',
    element: createProtectedRoute(<NZAssessmentResultAnalysis />, {
      tier: 'premium',
      meta: {
        title: 'NZ Assessment Result Analysis',
        region: 'NZ',
        intent: 'analysis',
        aiTags: ['assessment', 'result', 'analysis'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'assessment-result-analysis'.
        toolId: 'nz-assessment-result-analysis',
        category: 'assessments',
      },
    }),
  },

  // Pathway & planning
  {
    id: 'nz.pathway_dreams',
    domain: 'newzealand',
    path: '/nz-pathway-dreams-engine',
    element: createProtectedRoute(<NZPathwayDreamsEngine />, {
      tier: 'premium',
      meta: {
        title: 'NZ Pathway Dreams Engine',
        region: 'NZ',
        intent: 'engine',
        aiTags: ['pathway', 'dreams'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'pathway-dreams-engine'.
        toolId: 'nz-pathway-dreams-engine',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.ita_simulator',
    domain: 'newzealand',
    path: '/nz-ita-simulator',
    element: createProtectedRoute(<NZITASimulator />, {
      tier: 'premium',
      meta: {
        title: 'NZ ITA Simulator',
        region: 'NZ',
        intent: 'simulator',
        aiTags: ['ita', 'invitation', 'simulator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'ita-simulator'.
        toolId: 'nz-ita-simulator',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.success_prediction',
    domain: 'newzealand',
    path: '/nz-success-prediction',
    element: createProtectedRoute(<NZSuccessPrediction />, {
      tier: 'premium',
      meta: {
        title: 'NZ Success Prediction',
        region: 'NZ',
        intent: 'prediction',
        aiTags: ['success', 'prediction'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'success-prediction'.
        toolId: 'nz-success-prediction',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.timeline_forecaster',
    domain: 'newzealand',
    path: '/nz-timeline-forecaster',
    element: createProtectedRoute(<NZTimelineForecaster />, {
      tier: 'premium',
      meta: {
        title: 'NZ Timeline Forecaster',
        region: 'NZ',
        intent: 'forecaster',
        aiTags: ['timeline', 'processing'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'timeline-forecaster'.
        toolId: 'nz-timeline-forecaster',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.dependency_optimizer',
    domain: 'newzealand',
    path: '/nz-dependency-optimizer',
    element: createProtectedRoute(<NZDependencyOptimizer />, {
      tier: 'premium',
      meta: {
        title: 'NZ Dependency Optimizer',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['dependency', 'optimizer'],
      },
    }),
  },
  {
    id: 'nz.pathway_engine',
    domain: 'newzealand',
    path: '/nz-pathway-engine',
    element: createProtectedRoute(<NZPathwayEngine />, {
      tier: 'premium',
      meta: {
        title: 'NZ Pathway Engine',
        region: 'NZ',
        intent: 'engine',
        aiTags: ['pathway', 'engine'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'pathway-engine'.
        toolId: 'nz-pathway-engine',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.skilled_migrant_points',
    domain: 'newzealand',
    path: '/nz-skilled-migrant-points',
    element: createProtectedRoute(<NZSkilledMigrantPoints />, {
      tier: 'premium',
      meta: {
        title: 'NZ Skilled Migrant Points',
        region: 'NZ',
        intent: 'calculator',
        aiTags: ['points', 'skilled migrant'],
        // Tool-usage tracking pilot (see docs on RouteMeta.toolId) — matches
        // the NewZealandToolsEcosystem.tsx catalog id 'smc-points'.
        toolId: 'nz-smc-points',
        category: 'calculators',
      },
    }),
  },

  // Document & validation
  {
    id: 'nz.bank_validator',
    domain: 'newzealand',
    path: '/nz-bank-validator',
    element: createProtectedRoute(<NZDocumentRequirementGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Bank Evidence Guide',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['bank', 'statement'],
        toolId: 'nz-bank-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.education_validator',
    domain: 'newzealand',
    path: '/nz-education-validator',
    element: createProtectedRoute(<NZDocumentRequirementGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Education Evidence Guide',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['education', 'credential'],
        toolId: 'nz-education-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.police_validator',
    domain: 'newzealand',
    path: '/nz-police-validator',
    element: createProtectedRoute(<NZDocumentRequirementGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Police Certificate Guide',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['police', 'certificate'],
        toolId: 'nz-police-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.marriage_validator',
    domain: 'newzealand',
    path: '/nz-marriage-validator',
    element: createProtectedRoute(<NZDocumentRequirementGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Marriage Evidence Guide',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['marriage', 'certificate'],
        toolId: 'nz-marriage-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.verification_dashboard',
    domain: 'newzealand',
    path: '/nz-verification-dashboard',
    element: createProtectedRoute(<NZVerificationDashboard />, {
      tier: 'premium',
      meta: {
        title: 'NZ Verification Dashboard',
        region: 'NZ',
        intent: 'dashboard',
        aiTags: ['verification', 'dashboard'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'verification-dashboard'.
        toolId: 'nz-verification-dashboard',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.doc_completeness',
    domain: 'newzealand',
    path: '/nz-document-completeness-checker',
    element: createProtectedRoute(<NZDocumentCompletenessChecker />, {
      tier: 'premium',
      meta: {
        title: 'NZ Document Completeness Checker',
        region: 'NZ',
        intent: 'checker',
        aiTags: ['document', 'completeness'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'doc-completeness'.
        toolId: 'nz-doc-completeness',
        category: 'documents',
      },
    }),
  },
  {
    id: 'nz.nzqa_eca',
    domain: 'newzealand',
    path: '/nz-nzqa-eca-tool',
    element: createProtectedRoute(<NZNZQAECATool />, {
      tier: 'premium',
      meta: {
        title: 'NZ NZQA ECA Tool',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['nzqa', 'eca', 'credential'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'nzqa-eca-tool'.
        toolId: 'nz-nzqa-eca-tool',
        category: 'documents',
      },
    }),
  },
  {
    id: 'nz.work_ref_checker',
    domain: 'newzealand',
    path: '/nz-work-ref-letter-checker',
    element: createProtectedRoute(<NZWorkRefLetterChecker />, {
      tier: 'premium',
      meta: {
        title: 'NZ Work Ref Letter Checker',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['work reference', 'letter'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'work-ref-checker'.
        toolId: 'nz-work-ref-checker',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.photo_validator',
    domain: 'newzealand',
    path: '/nz-photo-document-validator',
    element: createProtectedRoute(<NZDocumentRequirementGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Photo Evidence Guide',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['photo', 'document'],
        toolId: 'nz-photo-document-validator',
        category: 'validators',
      },
    }),
  },

  // Calculators & checkers
  {
    id: 'nz.maintenance_funds',
    domain: 'newzealand',
    path: '/nz-maintenance-funds-calculator',
    element: createProtectedRoute(<NZMaintenanceFundsCalculator />, {
      tier: 'premium',
      meta: {
        title: 'NZ Maintenance Funds Calculator',
        region: 'NZ',
        intent: 'calculator',
        aiTags: ['maintenance funds', 'settlement'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'maintenance-funds'.
        toolId: 'nz-maintenance-funds',
        category: 'financial',
      },
    }),
  },
  {
    id: 'nz.dependent_visa',
    domain: 'newzealand',
    path: '/nz-dependent-visa-calculator',
    element: createProtectedRoute(<NZDependentVisaCalculator />, {
      tier: 'premium',
      meta: {
        title: 'NZ Dependent Visa Calculator',
        region: 'NZ',
        intent: 'calculator',
        aiTags: ['dependent', 'visa'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'dependent-visa'.
        toolId: 'nz-dependent-visa',
        category: 'calculators',
      },
    }),
  },
  {
    id: 'nz.global_salary',
    domain: 'newzealand',
    path: '/nz-global-salary-calculator',
    element: createProtectedRoute(<NZGlobalSalaryCalculator />, {
      tier: 'premium',
      meta: {
        title: 'NZ Global Salary Calculator',
        region: 'NZ',
        intent: 'calculator',
        aiTags: ['salary', 'global'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'global-salary'.
        toolId: 'nz-global-salary',
        category: 'financial',
      },
    }),
  },
  {
    id: 'nz.tax_estimator',
    domain: 'newzealand',
    path: '/nz-tax-estimator',
    element: createProtectedRoute(<NZTaxEstimator />, {
      tier: 'premium',
      meta: {
        title: 'NZ Tax Estimator',
        region: 'NZ',
        intent: 'calculator',
        aiTags: ['tax', 'estimator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'tax-estimator'.
        toolId: 'nz-tax-estimator',
        category: 'financial',
      },
    }),
  },
  {
    id: 'nz.health_coverage',
    domain: 'newzealand',
    path: '/nz-health-coverage',
    element: createProtectedRoute(<NZHealthCoverage />, {
      tier: 'premium',
      meta: {
        title: 'NZ Health Coverage',
        region: 'NZ',
        intent: 'checker',
        aiTags: ['health', 'coverage'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'health-coverage'.
        toolId: 'nz-health-coverage',
        category: 'financial',
      },
    }),
  },
  {
    id: 'nz.citizenship_eligibility',
    domain: 'newzealand',
    path: '/nz-citizenship-eligibility',
    element: createProtectedRoute(<NZCitizenshipEligibility />, {
      tier: 'premium',
      meta: {
        title: 'NZ Citizenship Eligibility',
        region: 'NZ',
        intent: 'checker',
        aiTags: ['citizenship', 'eligibility'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'citizenship-eligibility'.
        toolId: 'nz-citizenship-eligibility',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.region_comparison',
    domain: 'newzealand',
    path: '/nz-region-comparison',
    element: createProtectedRoute(<NZRegionComparison />, {
      tier: 'premium',
      meta: {
        title: 'NZ Region Comparison',
        region: 'NZ',
        intent: 'comparison',
        aiTags: ['region', 'comparison'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'region-comparison'.
        toolId: 'nz-region-comparison',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.accredited_employer_db',
    domain: 'newzealand',
    path: '/nz-accredited-employer-database',
    element: createProtectedRoute(<NZAccreditedEmployerDatabase />, {
      tier: 'premium',
      meta: {
        title: 'NZ Accredited Employer Database',
        region: 'NZ',
        intent: 'database',
        aiTags: ['accredited employer', 'database'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'accredited-employer-db'.
        toolId: 'nz-accredited-employer-db',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.job_bank',
    domain: 'newzealand',
    path: '/nz-job-bank',
    element: createProtectedRoute(<NZJobBank />, {
      tier: 'premium',
      meta: {
        title: 'NZ Job Bank',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['job bank', 'employment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'job-bank'.
        toolId: 'nz-job-bank',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.occupation_demand',
    domain: 'newzealand',
    path: '/nz-occupation-demand-checker',
    element: createProtectedRoute(<NZOccupationDemandChecker />, {
      tier: 'premium',
      meta: {
        title: 'NZ Occupation Demand Checker',
        region: 'NZ',
        intent: 'checker',
        aiTags: ['occupation', 'demand'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'occupation-demand'.
        toolId: 'nz-occupation-demand',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.biometric_appointment',
    domain: 'newzealand',
    path: '/nz-biometric-appointment',
    element: createProtectedRoute(<NZBiometricAppointment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Biometric Appointment',
        region: 'NZ',
        intent: 'booking',
        aiTags: ['biometric', 'appointment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'biometric-appointment'.
        toolId: 'nz-biometric-appointment',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.residence_from_work',
    domain: 'newzealand',
    path: '/nz/residence-from-work',
    element: createProtectedRoute(<NZResidenceFromWork />, {
      tier: 'premium',
      meta: {
        title: 'NZ Residence from Work',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['residence', 'work'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'residence-from-work'.
        toolId: 'nz-residence-from-work',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.biometric_validator',
    domain: 'newzealand',
    path: '/nz-biometric-validator',
    element: createProtectedRoute(<NZBiometricValidator />, {
      tier: 'premium',
      meta: {
        title: 'NZ Biometric Validator',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['biometric', 'validator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'biometric-validator'.
        toolId: 'nz-biometric-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.accommodation_checker',
    domain: 'newzealand',
    path: '/nz-accommodation-checker',
    element: createProtectedRoute(<NZAccommodationChecker />, {
      tier: 'premium',
      meta: {
        title: 'NZ Accommodation Checker',
        region: 'NZ',
        intent: 'checker',
        aiTags: ['accommodation', 'checker'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'accommodation-checker'.
        toolId: 'nz-accommodation-checker',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.dli_validator',
    domain: 'newzealand',
    path: '/nz-dli-validator',
    element: createProtectedRoute(<NZDLIValidator />, {
      tier: 'premium',
      meta: {
        title: 'NZ DLI (NZQA) Validator',
        region: 'NZ',
        intent: 'validator',
        aiTags: ['dli', 'nzqa', 'validator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'dli-validator'.
        toolId: 'nz-dli-validator',
        category: 'validators',
      },
    }),
  },
  {
    id: 'nz.risk_assessment',
    domain: 'newzealand',
    path: '/nz-risk-assessment',
    element: createProtectedRoute(<NZRiskAssessment />, {
      tier: 'premium',
      meta: {
        title: 'NZ Risk Assessment',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['risk', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'risk-assessment'.
        toolId: 'nz-risk-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.work_to_residence',
    domain: 'newzealand',
    path: '/nz-work-to-residence',
    element: createProtectedRoute(<NZWorkToResidence />, {
      tier: 'premium',
      meta: {
        title: 'NZ Work to Residence',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['work to residence', 'wtr'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'work-to-residence'.
        toolId: 'nz-work-to-residence',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.accredited_employer',
    domain: 'newzealand',
    path: '/nz-accredited-employer',
    element: createProtectedRoute(<NZAccreditedEmployer />, {
      tier: 'premium',
      meta: {
        title: 'NZ Accredited Employer Finder',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['accredited employer', 'finder'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'accredited-employer'.
        toolId: 'nz-accredited-employer',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.study_permit',
    domain: 'newzealand',
    path: '/nz-study-permit',
    element: createProtectedRoute(<NZStudyPermit />, {
      tier: 'premium',
      meta: {
        title: 'NZ Study Permit Tool',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['study permit', 'student'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'study-permit'.
        toolId: 'nz-study-permit',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.business_visitor_guide',
    domain: 'newzealand',
    path: '/nz-business-visitor-guide',
    element: createProtectedRoute(<NZBusinessVisitorGuide />, {
      tier: 'premium',
      meta: {
        title: 'NZ Business Visitor Guide',
        region: 'NZ',
        intent: 'guide',
        aiTags: ['business visitor', 'guide'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'business-visitor'.
        toolId: 'nz-business-visitor',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.regional_visa_processing',
    domain: 'newzealand',
    path: '/nz-regional-visa-processing',
    element: createProtectedRoute(<NZRegionalVisaProcessing />, {
      tier: 'premium',
      meta: {
        title: 'NZ Regional Visa Processing',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['regional', 'processing', 'times'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'regional-visa-processing'.
        toolId: 'nz-regional-visa-processing',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.immigration_tech_data',
    domain: 'newzealand',
    path: '/nz-immigration-tech-data',
    element: createProtectedRoute(<NZImmigrationTechData />, {
      tier: 'premium',
      meta: {
        title: 'NZ Immigration Tech Data',
        region: 'NZ',
        intent: 'data',
        aiTags: ['tech', 'data', 'statistics'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'immigration-tech-data'.
        toolId: 'nz-immigration-tech-data',
        category: 'integration',
      },
    }),
  },
  {
    id: 'nz.youth_student_tools',
    domain: 'newzealand',
    path: '/nz-youth-student-tools',
    element: createProtectedRoute(<NZYouthStudentTools />, {
      tier: 'premium',
      meta: {
        title: 'NZ Youth & Student Tools',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['youth', 'student', 'tools'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'youth-student'.
        toolId: 'nz-youth-student',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.accommodation_housing',
    domain: 'newzealand',
    path: '/nz-accommodation-housing',
    element: createProtectedRoute(<NZAccommodationHousing />, {
      tier: 'premium',
      meta: {
        title: 'NZ Accommodation Housing',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['accommodation', 'housing'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'accommodation-housing'.
        toolId: 'nz-accommodation-housing',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.visa_expiry_extension',
    domain: 'newzealand',
    path: '/nz-visa-expiry-extension',
    element: createProtectedRoute(<NZVisaExpiryExtension />, {
      tier: 'premium',
      meta: {
        title: 'NZ Visa Expiry Extension',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['visa expiry', 'extension'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'visa-expiry-extension'.
        toolId: 'nz-visa-expiry-extension',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.pr_tools',
    domain: 'newzealand',
    path: '/nz-permanent-residence-tools',
    element: createProtectedRoute(<NZPermanentResidenceTools />, {
      tier: 'premium',
      meta: {
        title: 'NZ Permanent Residence Tools',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['permanent residence', 'pr'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'pr-tools'.
        toolId: 'nz-pr-tools',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.green_list_finder',
    domain: 'newzealand',
    path: '/nz-green-list-finder',
    element: createProtectedRoute(<NZGreenListFinder />, {
      tier: 'premium',
      meta: {
        title: 'NZ Green List Finder',
        region: 'NZ',
        intent: 'finder',
        aiTags: ['green list', 'finder'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'green-list-finder'.
        toolId: 'nz-green-list-finder',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.family_sponsor',
    domain: 'newzealand',
    path: '/nz-family-sponsor',
    element: createProtectedRoute(<NZFamilySponsor />, {
      tier: 'premium',
      meta: {
        title: 'NZ Family Sponsor',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['family', 'sponsor'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'family-sponsor'.
        toolId: 'nz-family-sponsor',
        category: 'planning',
      },
    }),
  },
  {
    id: 'nz.straight_to_residence',
    domain: 'newzealand',
    path: '/nz-straight-to-residence',
    element: createProtectedRoute(<NZStraightToResidence />, {
      tier: 'premium',
      meta: {
        title: 'NZ Straight to Residence Evaluator',
        region: 'NZ',
        intent: 'tool',
        aiTags: ['straight to residence', 'evaluator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'straight-to-residence'.
        toolId: 'nz-straight-to-residence',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.pathway_analytics',
    domain: 'newzealand',
    path: '/nz-pathway-analytics',
    element: createProtectedRoute(<NZPathwayAnalytics />, {
      tier: 'premium',
      meta: {
        title: 'NZ Pathway Analytics',
        region: 'NZ',
        intent: 'assessment',
        aiTags: ['pathway', 'analytics', 'smc', 'green list', 'aewv'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'pathway-analytics'.
        toolId: 'nz-pathway-analytics',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'nz.temporary_worker_routes',
    domain: 'newzealand',
    path: '/nz-temporary-worker-routes',
    element: createProtectedRoute(<NZTemporaryWorkerRoutes />, {
      tier: 'premium',
      meta: {
        title: 'NZ Temporary Worker Routes',
        region: 'NZ',
        intent: 'guide',
        aiTags: ['temporary worker', 'aewv', 'working holiday'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // NewZealandToolsEcosystem.tsx catalog id 'temporary-worker'.
        toolId: 'nz-temporary-worker',
        category: 'assessments',
      },
    }),
  },

  // ------------------------------
  // Phase B — 21 previously-unbuilt ecosystem entries, now real
  // ------------------------------
  {
    id: 'nz.healthcare_routes',
    domain: 'newzealand',
    path: '/nz-professional-healthcare-routes',
    element: createProtectedRoute(<NZProfessionalHealthcareRoutes />, {
      tier: 'premium',
      meta: { title: 'NZ Professional Healthcare Routes', region: 'NZ', intent: 'guide', aiTags: ['healthcare', 'nurses', 'doctors', 'green list'], toolId: 'nz-healthcare-routes', category: 'assessments' },
    }),
  },
  {
    id: 'nz.dependant_family_tools',
    domain: 'newzealand',
    path: '/nz-dependant-family-tools',
    element: createProtectedRoute(<NZDependantFamilyTools />, {
      tier: 'premium',
      meta: { title: 'NZ Dependant & Family Tools', region: 'NZ', intent: 'guide', aiTags: ['family', 'dependant', 'parents'], toolId: 'nz-dependant-family', category: 'planning' },
    }),
  },
  {
    id: 'nz.caregiver',
    domain: 'newzealand',
    path: '/nz-caregiver',
    element: createProtectedRoute(<NZCaregiverPathways />, {
      tier: 'premium',
      meta: { title: 'NZ Caregiver Pathways', region: 'NZ', intent: 'guide', aiTags: ['caregiver', 'parent visitor', 'care workforce'], toolId: 'nz-caregiver', category: 'assessments' },
    }),
  },
  {
    id: 'nz.caregiver_programs',
    domain: 'newzealand',
    path: '/nz-caregiver-programs',
    element: <Navigate to="/nz-caregiver" replace />,
  },
  {
    id: 'nz.agri_food',
    domain: 'newzealand',
    path: '/nz-agri-food',
    element: createProtectedRoute(<NZAgriFoodSeasonalWork />, {
      tier: 'premium',
      meta: { title: 'NZ Agri-Food & Seasonal Work', region: 'NZ', intent: 'guide', aiTags: ['agriculture', 'seasonal work', 'rse'], toolId: 'nz-agri-food', category: 'assessments' },
    }),
  },
  {
    id: 'nz.agrifood_pilot',
    domain: 'newzealand',
    path: '/nz-agrifood-pilot',
    element: <Navigate to="/nz-agri-food" replace />,
  },
  {
    id: 'nz.self_employed',
    domain: 'newzealand',
    path: '/nz-self-employed',
    element: createProtectedRoute(<NZSelfEmployedEntrepreneur />, {
      tier: 'premium',
      meta: { title: 'NZ Self-Employed & Entrepreneur', region: 'NZ', intent: 'guide', aiTags: ['entrepreneur', 'self-employed', 'investor'], toolId: 'nz-self-employed', category: 'assessments' },
    }),
  },
  {
    id: 'nz.self_employed_program',
    domain: 'newzealand',
    path: '/nz-self-employed-program',
    element: <Navigate to="/nz-self-employed" replace />,
  },
  {
    id: 'nz.visa_fee_calculator',
    domain: 'newzealand',
    path: '/nz-visa-fee-calculator',
    element: createProtectedRoute(<NZVisaFeeCalculator />, {
      tier: 'premium',
      meta: { title: 'NZ Visa Fee Calculator', region: 'NZ', intent: 'calculator', aiTags: ['fees', 'ivl', 'calculator'], toolId: 'nz-visa-fee-calculator', category: 'financial' },
    }),
  },
  {
    id: 'nz.document_authentication',
    domain: 'newzealand',
    path: '/nz-document-authentication',
    element: createProtectedRoute(<NZDocumentAuthentication />, {
      tier: 'premium',
      meta: { title: 'NZ Document Authentication', region: 'NZ', intent: 'guide', aiTags: ['apostille', 'authentication', 'documents'], toolId: 'nz-document-authentication', category: 'documents' },
    }),
  },
  {
    id: 'nz.skilled_migrant_eligibility',
    domain: 'newzealand',
    path: '/nz-skilled-migrant-eligibility',
    element: createProtectedRoute(<NZSkilledMigrantEligibility />, {
      tier: 'premium',
      meta: { title: 'NZ Skilled Migrant Eligibility', region: 'NZ', intent: 'checker', aiTags: ['smc', 'eligibility', 'minimum requirements'], toolId: 'nz-skilled-migrant-eligibility', category: 'assessments' },
    }),
  },
  {
    id: 'nz.green_list_trades',
    domain: 'newzealand',
    path: '/nz-green-list-trades',
    element: createProtectedRoute(<NZGreenListTrades />, {
      tier: 'premium',
      meta: { title: 'NZ Green List Trades', region: 'NZ', intent: 'finder', aiTags: ['green list', 'trades', 'work to residence'], toolId: 'nz-green-list-trades', category: 'assessments' },
    }),
  },
  {
    id: 'nz.regional_skilled_work',
    domain: 'newzealand',
    path: '/nz-regional-skilled-work',
    element: createProtectedRoute(<NZRegionalRuralWork />, {
      tier: 'premium',
      meta: { title: 'NZ Regional Skilled Work', region: 'NZ', intent: 'guide', aiTags: ['regional', 'rural', 'sector agreements'], toolId: 'nz-regional-skilled-work', category: 'assessments' },
    }),
  },
  {
    id: 'nz.rural_regional_visa',
    domain: 'newzealand',
    path: '/nz-rural-regional-visa',
    element: <Navigate to="/nz-regional-skilled-work" replace />,
  },
  {
    id: 'nz.translation_guide',
    domain: 'newzealand',
    path: '/nz-translation-guide',
    element: createProtectedRoute(<NZTranslationGuide />, {
      tier: 'premium',
      meta: { title: 'NZ Translation & Notarisation Guide', region: 'NZ', intent: 'guide', aiTags: ['translation', 'notarisation', 'documents'], toolId: 'nz-translation-guide', category: 'documents' },
    }),
  },
  {
    id: 'nz.english_requirement',
    domain: 'newzealand',
    path: '/nz-english-requirement',
    element: createProtectedRoute(<NZEnglishRequirement />, {
      tier: 'premium',
      meta: { title: 'NZ English Language Requirement', region: 'NZ', intent: 'checker', aiTags: ['english', 'ielts', 'pte', 'toefl'], toolId: 'nz-english-requirement', category: 'calculators' },
    }),
  },
  {
    id: 'nz.points_calculator_alt',
    domain: 'newzealand',
    path: '/nz-points-calculator',
    element: createProtectedRoute(<NZPointsCalculatorAlt />, {
      tier: 'premium',
      meta: { title: 'NZ Points Calculator', region: 'NZ', intent: 'redirect', aiTags: ['points', 'smc', 'calculator'], toolId: 'nz-points-calculator', category: 'calculators' },
    }),
  },
  {
    id: 'nz.draw_predictor',
    domain: 'newzealand',
    path: '/nz-draw-predictor',
    element: createProtectedRoute(<NZDrawHistory />, {
      tier: 'premium',
      meta: { title: 'NZ EOI Selection — How It Works', region: 'NZ', intent: 'guide', aiTags: ['eoi', 'selection', 'smc'], toolId: 'nz-draw-predictor', category: 'integration' },
    }),
  },
  {
    id: 'nz.inz_webform_helper',
    domain: 'newzealand',
    path: '/nz-inz-webform-helper',
    element: createProtectedRoute(<NZInzWebformHelper />, {
      tier: 'premium',
      meta: { title: 'NZ INZ Webform Helper', region: 'NZ', intent: 'tool', aiTags: ['webform', 'message', 'draft'], toolId: 'nz-inz-webform', category: 'integration' },
    }),
  },
  {
    id: 'nz.panel_physician_finder',
    domain: 'newzealand',
    path: '/nz-panel-physician-finder',
    element: createProtectedRoute(<NZPanelPhysicianFinder />, {
      tier: 'premium',
      meta: { title: 'NZ Panel Physician Finder', region: 'NZ', intent: 'guide', aiTags: ['panel physician', 'medical exam'], toolId: 'nz-panel-physician', category: 'integration' },
    }),
  },
  {
    id: 'nz.immigration_eligibility_quiz',
    domain: 'newzealand',
    path: '/nz-immigration-eligibility-quiz',
    element: createProtectedRoute(<NZImmigrationEligibilityQuiz />, {
      tier: 'premium',
      meta: { title: 'NZ Immigration Eligibility Quiz', region: 'NZ', intent: 'quiz', aiTags: ['eligibility', 'quiz', 'routing'], toolId: 'nz-eligibility-quiz', category: 'assessments' },
    }),
  },
];